import { collectBrowserInfo } from './methods';
import { createDeposit, getDeposit } from './payments-api';
import type { CreateDepositResponse, DepositMethodId, DepositOrder } from './types';

export const PENDING_ORDER_KEY = 'payments:pendingOrderId';
export const FINAL_STATUSES: ReadonlySet<string> = new Set([
  'APPROVED', 'DECLINED', 'ERROR', 'EXPIRED', 'MISMATCH', 'PARTIALLY_REFUNDED', 'REFUNDED',
]);

export interface StartDepositOptions {
  method: DepositMethodId;
  /** Catalogue product to buy (priced by the server). */
  productId?: string;
  /** Custom EUR cents — only when the game allows custom amounts. */
  amountCents?: number;
  currency: string;
  /** Same-origin path; backend appends ?order=<orderId>. Default /payment-result. */
  returnPath?: string;
}

/** Create the order, remember it, and send the browser to the gateway. */
export async function startDeposit(opts: StartDepositOptions): Promise<CreateDepositResponse> {
  const res = await createDeposit({
    method: opts.method,
    ...(opts.productId ? { productId: opts.productId } : { amountCents: opts.amountCents }),
    currency: opts.currency,
    browser: collectBrowserInfo(),
    ...(opts.returnPath ? { returnPath: opts.returnPath } : {}),
  });
  try { sessionStorage.setItem(PENDING_ORDER_KEY, res.orderId); } catch { /* private mode */ }
  window.location.assign(res.redirectUrl);
  return res;
}

/** Order id after returning from the gateway: `?order=` first, then sessionStorage. */
export function readReturnedOrderId(search: string = window.location.search): string | null {
  const fromUrl = new URLSearchParams(search).get('order');
  if (fromUrl) return fromUrl;
  try { return sessionStorage.getItem(PENDING_ORDER_KEY); } catch { return null; }
}

export function clearPendingOrder(): void {
  try { sessionStorage.removeItem(PENDING_ORDER_KEY); } catch { /* ignore */ }
}

export interface PollOptions {
  intervalMs?: number;
  maxTries?: number;
  onUpdate?: (order: DepositOrder) => void;
  signal?: AbortSignal;
}

/**
 * Poll until the order reaches a final status (or maxTries is exhausted, in which
 * case the last seen order — possibly still PENDING — is returned).
 */
export async function pollDepositStatus(orderId: string, opts: PollOptions = {}): Promise<DepositOrder> {
  const { intervalMs = 2000, maxTries = 15, onUpdate, signal } = opts;
  let last: DepositOrder | null = null;
  for (let i = 0; i < maxTries; i++) {
    if (signal?.aborted) break;
    last = await getDeposit(orderId);
    onUpdate?.(last);
    if (FINAL_STATUSES.has(last.status)) {
      clearPendingOrder();
      return last;
    }
    if (i < maxTries - 1) await new Promise((r) => setTimeout(r, intervalMs));
  }
  if (!last) throw new Error('Polling aborted');
  return last;
}
